import { useState } from 'react'
import { useGetSocialSigninStartUrlLazyQuery } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { localStorageKeys } from '@sketch/constants'
import { setItem } from '@sketch/utils'
import * as Sentry from '@sentry/browser'

export const useGetSocialSignIn = () => {
  const { showToast } = useToast()
  const [isPersistentLoading, setIsPersistentLoading] = useState(false)

  const [getSocialSigninStartUrl, { loading: mutationLoading }] =
    useGetSocialSigninStartUrlLazyQuery({
      onCompleted: data => {
        const { url, state } = data.socialSigninStartUrl

        /*
         * "state" is stored in local storage and later sent back
         * to the BE to trade for the proper JWT token.
         */
        state && setItem(localStorageKeys.socialState, state)

        /**
         * Safari opens a Apple Sign in Pop-up that does not trigger any event when the user clicks "Cancel"
         * So we need to do this workaround to be able to reset the loading state.
         * When the user clicks cancel the window is focused and we use that to reset the loading state
         */
        const handleFocus = () => {
          setIsPersistentLoading(false)
          window.removeEventListener('focus', handleFocus)
        }

        window.addEventListener('focus', handleFocus)

        window.location.href = url
      },
      onError: ({ message }) => {
        setIsPersistentLoading(false)
        showToast('Something went wrong', 'negative')
        Sentry.captureMessage(`Social Sign In Error: ${message}`)
      },
      fetchPolicy: 'network-only',
    })

  const handleGetSocialSigninStartUrl = (
    ...args: Parameters<typeof getSocialSigninStartUrl>
  ) => {
    setIsPersistentLoading(true)
    return getSocialSigninStartUrl(...args)
  }

  return {
    getSocialSigninStartUrl: handleGetSocialSigninStartUrl,
    loading: isPersistentLoading || mutationLoading,
  }
}
